import cls from 'classnames'
import Image from 'next/image'
import { inflect } from 'inflection'
import Thumbnail from 'components/Thumbnail'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  DiscoverEngagementsQueryResult,
  FilterableEngagementField,
  SortableEngagementField,
  SortDirection,
  useDiscoverEngagementsQuery
} from 'graphql/generated'
import { useIsLoggedIn, useQueryErrorHandler } from 'hooks'
import moment from 'moment'
import RSVPButton from 'components/events/EventButtons/RSVPButton'
import ModalTrigger from 'components/common/ModalTrigger'
import { EventInputModal } from 'components/events/EventInputModal/EventInputModal'
import Link from 'next/link'
import Button from 'components/Button'
import { ROOT_HOSTNAME } from 'lib/helpers'
import { faChevronCircleRight } from '@fortawesome/pro-duotone-svg-icons'
import StyledTooltip from 'components/Tooltip/Tooltip'
import PageScroll from 'components/common/PageScroll'
import { CenterContainer } from 'components/common/CenterContainer'
import Typography from 'components/common/Typography'

type TNode = NonNullable<
  DiscoverEngagementsQueryResult['discoverEngagements']
>['edges'][0]['node']
type TEventNode = Extract<TNode, { __typename: 'Event' }>

function isEventType(node: TNode): node is TEventNode {
  return node.__typename === 'Event'
}

function EventItem({
  event
}: {
  event: NonNullable<
    DiscoverEngagementsQueryResult['discoverEngagements']
  >['edges'][0]['node']
}) {
  if (!isEventType(event)) {
    return null
  }
  return (
    <div
      key={event.id}
      className={`flex flex-col justify-between space-y-5 h-full bg-white shadow-lg rounded-lg p-4`}
    >
      <div className="space-y-3">
        <Link
          className={cls(
            !event.thumb && 'bg-[#E7EFFD]',
            ' w-[264px] h-[133px] flex justify-center items-center relative'
          )}
          href={event.publicUrl}
        >
          {event.thumb ? (
            <Image
              alt="Event thumbnail"
              src={event.thumb}
              fill
              sizes="25vw"
              className="object-cover"
            />
          ) : (
            <Image
              alt="Event thumbnail placeholder"
              src="/static/events/robot_placeholder.png"
              width={110}
              height={68}
              className="-mt-3"
            />
          )}

          {!!event.collective?.thumb && (
            <StyledTooltip
              arrow
              title={`${event.collective.name} Collective`.replace(
                /dao dao/i,
                'DAO'
              )}
            >
              <div className="absolute top-2 right-2 z-30">
                <Thumbnail
                  src={event.collective.thumb}
                  initials={event.collective.name}
                  className="h-8 w-8 rounded-full overflow-hidden border border-white shadow"
                />
              </div>
            </StyledTooltip>
          )}
        </Link>

        <div className="truncate">
          <Link
            className="text-gray-800 text-sm font-semibold"
            href={event.publicUrl}
          >
            {event.title}
          </Link>
          <p className="text-gray-500 text-xs font-light">
            {moment(event.startTime)
              .locale('en-abbrv')
              .format('ddd MMM D \u00b7 h:mma')}
          </p>
        </div>

        <div>
          {!!event.collective && (
            <Link
              translate="no"
              className="text-xs font-medium text-gray-700"
              href={event.collective.publicUrl}
            >
              {event.collective.name}
            </Link>
          )}
          <p className="text-xs font-light text-gray-500">
            {`${event.counts.attending} ${inflect(
              'attendees',
              event.counts.attending
            )}`}
            {` \u00b7 `}
            {event.formatLabel}
          </p>
        </div>

        <div className="space-y-1">
          <p className="text-xs font-light text-gray-500">{`Hosted by: `}</p>

          <Link
            href={`${ROOT_HOSTNAME}/profiles/${event.user.id}`}
            className="flex flex-row items-center space-x-2 group"
          >
            <Thumbnail
              src={event.user.thumb}
              className="h-6 w-6 rounded-full overflow-hidden"
            />

            <p className="text-xs font-light text-gray-500">
              {event.user.displayName}
            </p>

            <FontAwesomeIcon
              icon={faChevronCircleRight}
              className="opacity-20 group-hover:opacity-100 relative left-0 group-hover:left-1 transition-all"
            />
          </Link>
        </div>

        {event.speakers.length > 0 && (
          <div className="pt-3">
            <p className="text-xs font-light text-text-secondary leading-tight pb-1">{`${inflect(
              'Speaker',
              1
            )}:`}</p>

            <div className="space-y-1">
              {event.speakers.map(speaker => (
                <div
                  key={`speaker-${speaker.id}`}
                  className="flex items-center space-x-2"
                >
                  <Thumbnail
                    src={speaker.thumb}
                    className={'h-8 w-8 rounded-full overflow-hidden shrink-0'}
                  />

                  <p className="block text-xs font-bold">
                    {speaker.displayName}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {event.canJoinVideo ? (
        <Link href={`${event.publicUrl}/video`} passHref>
          <Button label="Enter Event" className="w-full py-3" />
        </Link>
      ) : (
        <RSVPButton event={event} size="md" className="w-full py-3" />
      )}
    </div>
  )
}

function DiscoverUpcomingEvents() {
  const isLoggedIn = useIsLoggedIn()
  const onError = useQueryErrorHandler(
    'Failed to load Discoverable Events. Please try again later.'
  )
  const { data } = useDiscoverEngagementsQuery({
    variables: {
      engagementConnection: { first: 20 },
      sort: {
        direction: SortDirection.ASCENDING,
        field: SortableEngagementField.START_TIME
      },
      filters: [
        FilterableEngagementField.STATUS_NOT_ENDED,
        FilterableEngagementField.TYPE_SOCIAL
      ]
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onError
  })

  const edges = data?.discoverEngagements?.edges
  const events = edges?.map(x => x.node).filter(isEventType) || []

  return (
    <div className="bg-gradient-to-r from-[#68A2FF] to-[#F0A6FD] p-10 overflow-x-hidden">
      <div className="flex flex-row items-center space-x-8 pb-10">
        <p className="text-lg font-bold text-white">Upcoming Crypto Events</p>

        {isLoggedIn && (
          <ModalTrigger
            trigger={onClick => (
              <button
                onClick={onClick}
                className="flex flex-row items-center space-x-2 border text-sm border-white rounded-md px-3 py-1 text-white"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Create Event</span>
              </button>
            )}
          >
            <EventInputModal />
          </ModalTrigger>
        )}
      </div>

      <PageScroll
        itemWidth={296}
        emptyStateContent={
          <CenterContainer className="min-h-xs -mt-12">
            <Typography color="white" size="heading">
              No Upcoming Events
            </Typography>

            {!isLoggedIn && (
              <span className="border text-sm border-white rounded-md px-3 py-1 text-white">
                Log In to Create an Event
              </span>
            )}
          </CenterContainer>
        }
        items={events.map((event, i) => (
          <EventItem event={event} key={`${event.id}_${i}`} />
        ))}
      />
    </div>
  )
}

export default DiscoverUpcomingEvents
